import React from 'react';
import {
  CheckCircle,
  Circle,
  AlertCircle,
  Clock,
  FileCheck,
  Shield,
  TrendingUp,
  AlertTriangle,
  Flag,
} from 'lucide-react';

const cardStyle = {
  background: '#fff',
  borderRadius: 8,
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  padding: 20,
};

// ─── SLB Track Config (shared) ──────────────────────────────────────────────────

const SLB_TRACKS = [
  {
    id: 'property_track',
    label: 'Property Track',
    color: '#1E40AF',
    categories: [
      { id: 'alta_title', label: 'ALTA Survey / Title Commitment', priority: 'Critical', required_for_offer: true },
      { id: 'phase1_esa', label: 'Phase I Environmental Site Assessment', priority: 'Critical', required_for_offer: true },
      { id: 'pca', label: 'Property Condition Assessment', priority: 'Critical', required_for_offer: true },
      { id: 'building_specs', label: 'Building Specifications', priority: 'Critical', required_for_offer: true },
      { id: 'zoning', label: 'Zoning and Use Permits', priority: 'High', required_for_offer: false },
      { id: 'market_rent', label: 'Market Rent Analysis', priority: 'High', required_for_offer: false },
      { id: 'property_tax', label: 'Property Tax Records', priority: 'High', required_for_offer: false },
      { id: 'liens', label: 'Existing Liens and Encumbrances', priority: 'High', required_for_offer: false },
      { id: 'property_insurance', label: 'Property Insurance History', priority: 'Standard', required_for_offer: false },
    ],
  },
  {
    id: 'tenant_credit_track',
    label: 'Tenant Credit Track',
    color: '#C2410C',
    categories: [
      { id: 'financial_statements', label: 'Financial Statements (3 Years)', priority: 'Critical', required_for_offer: true },
      { id: 'tax_returns', label: 'Tax Returns (Federal, 3 Years)', priority: 'Critical', required_for_offer: true },
      { id: 't12_pl', label: 'Trailing 12-Month P&L', priority: 'Critical', required_for_offer: true },
      { id: 'pe_sponsor', label: 'PE Sponsor Information', priority: 'Critical', required_for_offer: false },
      { id: 'customer_concentration', label: 'Customer Concentration Analysis', priority: 'High', required_for_offer: true },
      { id: 'lease_confirmation', label: 'Lease Rate Confirmation Letter', priority: 'High', required_for_offer: true },
      { id: 'key_contracts', label: 'Key Contracts (Top 3-5)', priority: 'High', required_for_offer: false },
      { id: 'debt_schedule', label: 'Debt Schedule', priority: 'High', required_for_offer: false },
      { id: 'industry_context', label: 'Industry / Market Context', priority: 'Standard', required_for_offer: false },
    ],
  },
];

const ALL_CATEGORIES = SLB_TRACKS.flatMap(t => t.categories);
const REQUIRED_FOR_OFFER = ALL_CATEGORIES.filter(c => c.required_for_offer);

function normCategory(str) {
  if (!str) return '';
  return str.toLowerCase().replace(/[\s_\-/]+/g, '');
}

function isDocMatchingCategory(doc, cat) {
  const docCat = normCategory(doc.category);
  const catId = normCategory(cat.id);
  const catLabel = normCategory(cat.label);
  return docCat === catId || docCat === catLabel || docCat.includes(catId) || catId.includes(docCat);
}

function getUploadedCategoryIds(documents) {
  const uploaded = new Set();
  for (const doc of documents || []) {
    for (const cat of ALL_CATEGORIES) {
      if (isDocMatchingCategory(doc, cat)) {
        uploaded.add(cat.id);
      }
    }
  }
  return uploaded;
}

// ─── 1. DualTrackDocumentPanel ──────────────────────────────────────────────────

function StatusIcon({ status }) {
  if (status === 'uploaded') return <CheckCircle size={16} color="#10b981" />;
  if (status === 'required') return <Circle size={16} color="#f59e0b" fill="#f59e0b" />;
  return <Circle size={16} color="#d1d5db" />;
}

function TrackPanel({ track, uploadedIds }) {
  const { label, color, categories } = track;
  const required = categories.filter(c => c.required_for_offer);
  const uploadedRequired = required.filter(c => uploadedIds.has(c.id));

  return (
    <div
      style={{
        flex: '1 1 320px',
        minWidth: 280,
        ...cardStyle,
        border: `2px solid ${color}`,
      }}
    >
      <h4 style={{ margin: '0 0 10px', color, fontSize: 15, fontWeight: 600 }}>{label}</h4>

      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
          {uploadedRequired.length} of {required.length} required documents uploaded
        </div>
        <div style={{ height: 6, borderRadius: 3, background: '#e5e7eb' }}>
          <div
            style={{
              height: '100%',
              borderRadius: 3,
              width: required.length ? `${(uploadedRequired.length / required.length) * 100}%` : '0%',
              background: color,
              transition: 'width 0.3s',
            }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {categories.map((cat) => {
          const isUploaded = uploadedIds.has(cat.id);
          const status = isUploaded ? 'uploaded' : cat.required_for_offer ? 'required' : 'optional';

          return (
            <div key={cat.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <StatusIcon status={status} />
              <span style={{ flex: 1, color: isUploaded ? '#374151' : '#9ca3af' }}>{cat.label}</span>
              <span
                style={{
                  fontSize: 10,
                  padding: '2px 6px',
                  borderRadius: 4,
                  fontWeight: 500,
                  background: cat.priority === 'Critical' ? '#fef2f2' : cat.priority === 'High' ? '#fffbeb' : '#f9fafb',
                  color: cat.priority === 'Critical' ? '#dc2626' : cat.priority === 'High' ? '#d97706' : '#6b7280',
                }}
              >
                {cat.priority}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function DualTrackDocumentPanel({ documents }) {
  const uploadedIds = getUploadedCategoryIds(documents);

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
      {SLB_TRACKS.map(track => (
        <TrackPanel key={track.id} track={track} uploadedIds={uploadedIds} />
      ))}
    </div>
  );
}

// ─── 2. DealGradeWidget ─────────────────────────────────────────────────────────

function getScoreColor(value, thresholds) {
  if (value >= thresholds.green) return '#10b981';
  if (value >= thresholds.amber) return '#f59e0b';
  return '#ef4444';
}

function getGradeStyle(grade) {
  const map = {
    A: { bg: '#dcfce7', color: '#166534' },
    B: { bg: '#dbeafe', color: '#1e40af' },
    C: { bg: '#fef3c7', color: '#92400e' },
    D: { bg: '#fee2e2', color: '#991b1b' },
  };
  return map[grade] || map.D;
}

export function DealGradeWidget({ deal }) {
  if (!deal || !deal.combined_deal_grade) return null;

  const lcr = deal.lease_coverage_ratio ?? 0;
  const propScore = deal.property_risk_score ?? 0;
  const tenantScore = deal.tenant_credit_score ?? 0;
  const grade = deal.combined_deal_grade;
  const gradeStyle = getGradeStyle(grade);

  return (
    <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', padding: '12px 16px' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Lease Coverage</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: getScoreColor(lcr, { green: 3.0, amber: 2.0 }) }}>
          {lcr.toFixed(1)}x
        </div>
      </div>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Property</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: getScoreColor(propScore, { green: 7, amber: 4 }) }}>
          {propScore}/10
        </div>
      </div>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Tenant Credit</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: getScoreColor(tenantScore, { green: 7, amber: 4 }) }}>
          {tenantScore}/10
        </div>
      </div>
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 48, height: 48, borderRadius: 8, background: gradeStyle.bg, marginLeft: 'auto',
        }}
      >
        <span style={{ fontSize: 24, fontWeight: 800, color: gradeStyle.color }}>{grade}</span>
      </div>
    </div>
  );
}

// ─── 3. ThirtyDayCloseTracker ───────────────────────────────────────────────────

const MILESTONES = [
  { key: 'intro_call', label: 'Intro Call' },
  { key: 'offer_issued', label: 'Offer Issued' },
  { key: 'under_contract', label: 'Under Contract' },
  { key: 'closed', label: 'Closed' },
];

export function ThirtyDayCloseTracker({ deal, onMilestoneUpdate }) {
  const createdAt = deal?.createdAt || deal?.created_at;
  const days = createdAt ? Math.floor((new Date() - new Date(createdAt)) / (1000 * 60 * 60 * 24)) : 0;
  const color = days > 25 ? '#ef4444' : days >= 20 ? '#f59e0b' : '#10b981';
  const pct = Math.min(days / 30, 1) * 100;
  const milestoneDates = deal?.milestone_dates || {};
  const nextIdx = MILESTONES.findIndex(m => !milestoneDates[m.key]);

  return (
    <div style={{ ...cardStyle, padding: '12px 16px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <Clock size={16} color={color} />
        <div style={{ flex: 1 }}>
          <div style={{ height: 8, borderRadius: 4, background: '#e5e7eb' }}>
            <div style={{ height: '100%', borderRadius: 4, width: `${pct}%`, background: color, transition: 'width 0.3s' }} />
          </div>
        </div>
        <span style={{ fontSize: 13, fontWeight: 600, color }}>{days} / 30 days</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {MILESTONES.map((m, idx) => {
          const date = milestoneDates[m.key];
          const isNext = idx === nextIdx;
          return (
            <div
              key={m.key}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                padding: '6px 10px', borderRadius: 6, minWidth: 100, flex: '1 1 0',
                background: date ? '#f0fdf4' : '#f9fafb',
                border: isNext ? '1px dashed #f59e0b' : '1px solid #e5e7eb',
              }}
            >
              <span style={{ fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 2 }}>{m.label}</span>
              {date ? (
                <span style={{ fontSize: 11, color: '#10b981' }}>
                  {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              ) : isNext ? (
                <button
                  onClick={() => onMilestoneUpdate && onMilestoneUpdate(m.key)}
                  style={{
                    fontSize: 10, padding: '2px 8px', borderRadius: 4, border: 'none',
                    background: '#f59e0b', color: '#fff', cursor: 'pointer', fontWeight: 600, marginTop: 2,
                  }}
                >
                  Mark milestone
                </button>
              ) : (
                <span style={{ fontSize: 11, color: '#9ca3af' }}>--</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── 4. OfferReadinessAlert ─────────────────────────────────────────────────────

export function OfferReadinessAlert({ documents }) {
  const uploadedIds = getUploadedCategoryIds(documents);
  const missing = REQUIRED_FOR_OFFER.filter(c => !uploadedIds.has(c.id));
  const allPresent = missing.length === 0;

  const bannerBg = allPresent ? '#f0fdf4' : '#fffbeb';
  const bannerBorder = allPresent ? '#86efac' : '#fcd34d';
  const bannerColor = allPresent ? '#166534' : '#92400e';
  const Icon = allPresent ? FileCheck : AlertCircle;

  return (
    <div style={{ ...cardStyle, background: bannerBg, border: `1px solid ${bannerBorder}`, padding: '14px 20px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: missing.length ? 10 : 0 }}>
        <Icon size={18} color={bannerColor} />
        <span style={{ fontSize: 14, fontWeight: 600, color: bannerColor }}>
          {allPresent ? 'Ready to Offer' : `${missing.length} of ${REQUIRED_FOR_OFFER.length} required documents missing`}
        </span>
      </div>
      {missing.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 28, listStyle: 'disc' }}>
          {missing.map(c => (
            <li key={c.id} style={{ fontSize: 13, color: '#92400e', marginBottom: 3 }}>{c.label}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── 5. SaleLeasebackReportLayout ──────────────────────────────────────────────

function reportScoreColor(score) {
  if (score == null) return '#6b7280';
  if (score >= 7) return '#10b981';
  if (score >= 4) return '#f59e0b';
  return '#ef4444';
}

function lcrColor(lcr) {
  if (lcr == null) return '#6b7280';
  if (lcr >= 3.0) return '#10b981';
  if (lcr >= 2.0) return '#f59e0b';
  return '#ef4444';
}

function lcrInterpretation(lcr) {
  if (lcr == null) return '—';
  if (lcr >= 4.0) return 'Excellent';
  if (lcr >= 3.0) return 'Strong';
  if (lcr >= 2.5) return 'Adequate';
  if (lcr >= 2.0) return 'Thin';
  return 'Inadequate';
}

function severityOrder(severity) {
  const map = { Critical: 0, High: 1, Medium: 2, Low: 3 };
  return map[severity] ?? 4;
}

function severityBadgeStyle(severity) {
  const styles = {
    Critical: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' },
    High: { background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' },
    Medium: { background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd' },
    Low: { background: '#f9fafb', color: '#6b7280', border: '1px solid #e5e7eb' },
  };
  return styles[severity] || styles.Low;
}

function recommendationColor(rec) {
  if (!rec) return '#6b7280';
  const upper = rec.toUpperCase();
  if (upper.includes('FULL PRICE')) return '#10b981';
  if (upper.includes('WITH CONDITIONS')) return '#f59e0b';
  if (upper.includes('REDUCED PRICE')) return '#f59e0b';
  if (upper.includes('HOLD')) return '#d97706';
  if (upper.includes('PASS')) return '#ef4444';
  return '#6b7280';
}

function ReportScoreBadge({ score, label }) {
  const color = reportScoreColor(score);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: color + '18',
          border: `2px solid ${color}`,
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 700, color }}>{score != null ? score : '—'}</span>
      </div>
      <span style={{ fontSize: 12, color: '#6b7280' }}>{label}</span>
    </div>
  );
}

function ReportFieldRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: 13 }}>
      <span style={{ color: '#6b7280' }}>{label}</span>
      <span style={{ fontWeight: 500, color: '#111827' }}>{value ?? '—'}</span>
    </div>
  );
}

export function SaleLeasebackReportLayout({ analysis }) {
  if (!analysis) return null;

  const property = analysis.property_track || analysis.property || {};
  const tenant = analysis.tenant_credit_track || analysis.tenant_credit || {};
  const combined = analysis.combined || analysis;

  const propertyScore = property.risk_score ?? property.score ?? null;
  const tenantScore = tenant.credit_score ?? tenant.score ?? null;
  const lcr = combined.lease_coverage_ratio ?? analysis.lease_coverage_ratio ?? null;
  const ebitda = combined.ebitda ?? tenant.ebitda ?? null;
  const annualRent = combined.annual_rent ?? analysis.annual_rent ?? null;
  const dealGrade = combined.deal_grade ?? analysis.deal_grade ?? null;
  const crossTrackFlags = combined.cross_track_flags || analysis.cross_track_flags || [];
  const recommendation = combined.recommendation ?? analysis.recommendation ?? null;
  const executiveSummary = combined.executive_summary ?? analysis.executive_summary ?? null;
  const allFlags = analysis.flags || combined.flags || [];

  const sortedFlags = [...allFlags].sort((a, b) => severityOrder(a.severity) - severityOrder(b.severity));

  const gradeStyle = getGradeStyle(dealGrade);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* ── Two-column top section ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        {/* LEFT: Property Track */}
        <div style={{ ...cardStyle, flex: '1 1 320px', minWidth: 280, border: '2px solid #1E40AF' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <Shield size={16} color="#1E40AF" />
            <h4 style={{ margin: 0, color: '#1E40AF', fontSize: 15, fontWeight: 600 }}>Property Track</h4>
          </div>

          <ReportScoreBadge score={propertyScore} label="Property Risk Score (1-10)" />

          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 10, marginTop: 6 }}>
            <ReportFieldRow label="Environmental Status" value={property.environmental_status ?? property.environmental} />
            <ReportFieldRow label="Physical Condition" value={property.physical_condition ?? property.condition_summary} />
            <ReportFieldRow label="Building Specs" value={property.building_specs ?? property.specs_highlights} />
            <ReportFieldRow label="Title Status" value={property.title_status ?? property.title} />
            <ReportFieldRow label="Zoning" value={property.zoning_status ?? property.zoning} />
          </div>
        </div>

        {/* RIGHT: Tenant Credit Track */}
        <div style={{ ...cardStyle, flex: '1 1 320px', minWidth: 280, border: '2px solid #C2410C' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <TrendingUp size={16} color="#C2410C" />
            <h4 style={{ margin: 0, color: '#C2410C', fontSize: 15, fontWeight: 600 }}>Tenant Credit Track</h4>
          </div>

          <ReportScoreBadge score={tenantScore} label="Tenant Credit Score (1-10)" />

          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 10, marginTop: 6 }}>
            <ReportFieldRow label="Revenue" value={tenant.revenue} />
            <ReportFieldRow label="EBITDA" value={tenant.ebitda ?? tenant.ebitda_value} />
            <ReportFieldRow label="Margin" value={tenant.margin} />
            <ReportFieldRow label="Customer Concentration" value={tenant.customer_concentration} />
            <ReportFieldRow label="Debt Load" value={tenant.debt_load ?? tenant.debt} />
            <ReportFieldRow label="PE Sponsor" value={tenant.pe_sponsor ?? tenant.sponsor} />
            <ReportFieldRow label="Industry Context" value={tenant.industry_context ?? tenant.industry} />
          </div>
        </div>
      </div>

      {/* ── Lease Coverage Ratio Banner ── */}
      {lcr != null && (
        <div
          style={{
            ...cardStyle,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 20,
            padding: '20px 24px',
            border: `2px solid ${lcrColor(lcr)}20`,
            background: lcrColor(lcr) + '08',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Lease Coverage Ratio
            </div>
            <div style={{ fontSize: 36, fontWeight: 800, color: lcrColor(lcr) }}>
              {lcr.toFixed(2)}x
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: lcrColor(lcr), marginTop: 2 }}>
              {lcrInterpretation(lcr)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 24 }}>
            {ebitda != null && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#6b7280' }}>EBITDA</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
                  {typeof ebitda === 'number' ? `$${(ebitda / 1e6).toFixed(1)}M` : ebitda}
                </div>
              </div>
            )}
            {annualRent != null && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#6b7280' }}>Annual Rent</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
                  {typeof annualRent === 'number' ? `$${(annualRent / 1e6).toFixed(1)}M` : annualRent}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Combined Analysis Section ── */}
      <div style={{ ...cardStyle, padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          {dealGrade && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 44,
                height: 44,
                borderRadius: 8,
                background: gradeStyle.bg,
              }}
            >
              <span style={{ fontSize: 22, fontWeight: 800, color: gradeStyle.color }}>{dealGrade}</span>
            </div>
          )}
          {recommendation && (
            <div
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                background: recommendationColor(recommendation) + '14',
                border: `1px solid ${recommendationColor(recommendation)}40`,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 700, color: recommendationColor(recommendation), textTransform: 'uppercase' }}>
                {recommendation}
              </span>
            </div>
          )}
        </div>

        {crossTrackFlags.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Cross-Track Flags</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {crossTrackFlags.map((flag, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <AlertTriangle size={12} color="#f59e0b" />
                  <span style={{ color: '#374151' }}>{typeof flag === 'string' ? flag : flag.description || flag.text || '—'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {executiveSummary && (
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Executive Summary</div>
            <p style={{ margin: 0, fontSize: 13, color: '#4b5563', lineHeight: 1.6 }}>{executiveSummary}</p>
          </div>
        )}
      </div>

      {/* ── Full Flag Register ── */}
      {sortedFlags.length > 0 && (
        <div style={{ ...cardStyle, padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Flag size={16} color="#374151" />
            <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#374151' }}>Flag Register</h4>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>({sortedFlags.length})</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sortedFlags.map((flag, idx) => {
              const badgeStyle = severityBadgeStyle(flag.severity);
              return (
                <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13 }}>
                  <span
                    style={{
                      ...badgeStyle,
                      fontSize: 10,
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: 4,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      marginTop: 1,
                    }}
                  >
                    {flag.severity || 'Info'}
                  </span>
                  <span style={{ color: '#374151' }}>{flag.description || flag.text || flag.message || '—'}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
